/**
 * Every cheque shown to a person is shown as euros: "€250K – €3M", with what
 * is worth knowing about it ("(from $500K–$7M)", "(Dealroom)", "(est. from 7
 * rounds)") kept apart so a surface can set it in a fainter colour. The stored
 * values never change; this is a reading of them.
 */
/**
 * Euros per one unit of each currency, as of 2026-10-21. Deliberately rough: a
 * cheque is a range stated to one or two figures, so a few percent of
 * exchange-rate drift moves nothing a reader acts on. Update by hand when a
 * rate has moved enough to matter.
 */
export const EUR_PER_UNIT = {
    EUR: 1,
    USD: 0.92,
    GBP: 1.17,
    CHF: 1.05,
    SEK: 0.09,
};
const SYMBOL = { EUR: '€', USD: '$', GBP: '£' };
export function toEur(amount, currency) {
    const rate = EUR_PER_UNIT[currency.toUpperCase()];
    return rate === undefined ? null : amount * rate;
}
/**
 * The nearest value on the grid of one or two significant figures whose second
 * figure is 0 or 5: 1M, 1.5M, 2M … 9.5M, 10M, 15M. 6.1M is 6M, 7.3M is 7.5M,
 * 13M is 15M, 480K is 500K, 120M is 100M. A tie goes up.
 */
export function roundToGrid(value) {
    if (!(value > 0))
        return 0;
    const step = 5 * 10 ** (Math.floor(Math.log10(value)) - 1);
    // Whole steps, then back, so the result carries no float residue from the division.
    return Math.round(Math.round(value / step) * step * 1e6) / 1e6;
}
/** "250K", "2.5M", "1.5B": the suffix is chosen after rounding, so 999,999 reads "1M". */
export function formatGridAmount(value) {
    return suffixed(roundToGrid(value));
}
function suffixed(value) {
    if (value >= 1_000_000_000)
        return `${trim(value / 1_000_000_000)}B`;
    if (value >= 1_000_000)
        return `${trim(value / 1_000_000)}M`;
    if (value >= 1_000)
        return `${trim(value / 1_000)}K`;
    return String(value);
}
function trim(value) {
    return value.toFixed(1).replace(/\.0$/, '');
}
/**
 * "€N – €M", or a single "€N" where both ends round to the same figure: the
 * qualifier and the hover still carry the range as it was stated.
 */
function euroRange(min, max) {
    if (min === null && max === null)
        return '';
    if (min === null)
        return `up to €${formatGridAmount(max ?? 0)}`;
    if (max === null)
        return `€${formatGridAmount(min)}+`;
    const low = formatGridAmount(min);
    const high = formatGridAmount(max);
    return low === high ? `€${low}` : `€${low} – €${high}`;
}
/** "$500K–$7M", "CHF 250K–2M": the stated amounts as they were, for the qualifier and the hover. */
function originalRange(source, currency) {
    const symbol = SYMBOL[currency];
    const mark = (value) => `${symbol ?? ''}${suffixed(value)}`;
    const prefix = symbol === undefined && currency !== '' ? `${currency} ` : '';
    const { chequeMin: min, chequeMax: max } = source;
    if (min === null)
        return `${prefix}up to ${mark(max ?? 0)}`;
    if (max === null)
        return `${prefix}${mark(min)}+`;
    if (min === max)
        return `${prefix}${mark(min)}`;
    return `${prefix}${mark(min)}–${mark(max)}`;
}
function assemble(parts) {
    const text = parts.qualifier === '' ? parts.range : `${parts.range} ${parts.qualifier}`;
    return { ...parts, text };
}
/** The cheque somebody stated (the sheet, an edit, or Dealroom's band), in euros; null where none is. */
export function statedChequeDisplay(source) {
    const { chequeMin: min, chequeMax: max } = source;
    if (min === null && max === null)
        return null;
    const currency = (source.chequeCurrency ?? '').toUpperCase();
    const minEur = min === null ? null : toEur(min, currency);
    const maxEur = max === null ? null : toEur(max, currency);
    const converted = (min === null || minEur !== null) && (max === null || maxEur !== null);
    const origin = source.chequeFromDealroom ? 'Dealroom' : null;
    const original = originalRange(source, currency);
    const statedBy = `Stated as ${original}${origin === null ? '' : ', from Dealroom'}`;
    if (!converted) {
        // A currency with no rate, or none stated: the figures stand as written.
        const note = currency === '' ? 'currency not stated' : `no euro rate for ${currency}`;
        return assemble({
            range: original,
            qualifier: `(${[origin, note].filter((part) => part !== null).join(', ')})`,
            minEur: null,
            maxEur: null,
            detail: `${statedBy}; ${note}, so not converted.`,
            estimated: false,
        });
    }
    const notEuro = currency !== 'EUR';
    const words = [origin, notEuro ? `from ${original}` : null].filter((part) => part !== null);
    const rate = notEuro ? `; shown in euros at ${currency} 1 = €${EUR_PER_UNIT[currency]}` : '';
    return assemble({
        range: euroRange(minEur, maxEur),
        qualifier: words.length === 0 ? '' : `(${words.join(', ')})`,
        minEur,
        maxEur,
        detail: `${statedBy}${rate}. Rounded to one or two figures.`,
        estimated: false,
    });
}
/** The estimate from a fund's rounds as a euro range; null where there is none. */
export function impliedChequeDisplay(source) {
    const estimate = source.impliedCheque;
    if (estimate === null)
        return null;
    const rounds = `${estimate.rounds} round${estimate.rounds === 1 ? '' : 's'}`;
    return assemble({
        range: euroRange(estimate.amountEurLow, estimate.amountEurHigh),
        qualifier: `(est. from ${rounds})`,
        minEur: estimate.amountEurLow,
        maxEur: estimate.amountEurHigh,
        detail: `Estimated, not stated. The middle half of this fund's share of ${rounds} it was in ` +
            `(median about €${formatGridAmount(estimate.amountEur)}): each round's size divided by its ` +
            `investors, over rounds with up to three investors or that the fund led.`,
        estimated: true,
    });
}
/** What the fund's cheque column shows: the stated cheque, else the estimate, else nothing. */
export function chequeDisplay(source) {
    return statedChequeDisplay(source) ?? impliedChequeDisplay(source);
}
//# sourceMappingURL=chequeDisplay.js.map